/*
 * Description: Input Number Field Web Component
 * Purpose: A reusable input field component specifically designed for numeric input with DataStore integration
 * Author: Nuvemx
 * Created: 2026
 * 
 * Features:
 * - Displays a name/label for the input field
 * - Designed to be embedded within other components
 * - Reactive data binding with DataStore
 * - Automatic value synchronization
 * - Real-time validation and updates
 * - Server synchronization support
 * 
 * Usage:
 * <input-number-field label="Age" data-path="user.age"></input-number-field>
 * <input-number-field label="Price" data-path="product.price" min="0" max="1000"></input-number-field>
 * 
 * Attributes:
 * - label: String - The label/name to display for this input field
 * - data-path: String - DataStore path for reactive binding (e.g., 'user.age')
 * - value: Number - Initial value (optional if using data-path)
 * - min: Number - Minimum allowed value
 * - max: Number - Maximum allowed value
 * - step: Number - Step increment (default: 1)
 * - placeholder: String - Placeholder text
 * - required: Boolean - Whether field is required
 * 
 * Methods:
 * - getValue(): Get current numeric value
 * - setValue(value): Set numeric value
 * - validate(): Validate current value
 * - reset(): Reset to initial value
 * 
 * Events:
 * - 'value-changed': Fired when value changes
 * - 'validation-error': Fired when validation fails
 * 
 */

class input_number_field extends HTMLElement {
    constructor() {
        super();
        this.initialValue = null;
        this.dataPath = null;
        this.isUpdating = false;
    }
    
    connectedCallback() {
        if (this.dataset.rendered) {
            return;
        }
        const value = this.getAttribute("value") || "";
        const label = this.getAttribute("label") || "Number Field";
        const dataPath = this.getAttribute("data-path");
        const min = this.getAttribute("min") || "";
        const max = this.getAttribute("max") || "";
        const step = this.getAttribute("step") || "1";
        const placeholder = this.getAttribute("placeholder") || "";
        const required = this.hasAttribute("required");
        
        // Generate unique ID for this instance
        const fieldId = `number-field-${Math.random().toString(36).substr(2, 9)}`;
        
        this.dataPath = dataPath;
        this.initialValue = parseFloat(value) || 0;
        
        this.innerHTML = `
            <div class="form-outline mb-3">
                <input 
                    type="number" 
                    id="${fieldId}" 
                    class="form-control" 
                    value="${value}"
                    min="${min}"
                    max="${max}"
                    step="${step}"
                    placeholder="${placeholder}"
                    ${required ? 'required' : ''}
                />
                <label class="form-label" for="${fieldId}">${label}</label>
                <div class="invalid-feedback"></div>
                <small class="form-text text-muted data-info" style="display: none;">
                    <i class="bx bx-data"></i> Synced with: <code>${dataPath}</code>
                </small>
            </div>
        `;
        
        this.input = this.querySelector('input');
        this.label = this.querySelector('label');
        this.feedback = this.querySelector('.invalid-feedback');
        this.dataInfo = this.querySelector('.data-info');
        
        // Show data path info if connected to DataStore
        if (dataPath && this.dataInfo) {
            this.dataInfo.style.display = 'block';
        }
        
        // Set up event listeners
        this.setupEventListeners();
        
        // Bind to DataStore if path is provided
        if (dataPath && window.dataStore) {
            this.bindToDataStore();
        }
        
        // Initial validation
        this.validate();
        this.dataset.rendered = "true";
    }
    
    setupEventListeners() {
        // Handle input changes
        this.input.addEventListener('input', (e) => {
            if (!this.isUpdating) {
                const numericValue = parseFloat(e.target.value);
                const isValid = this.validate();
                
                if (isValid && !isNaN(numericValue)) {
                    // Update DataStore if connected
                    if (this.dataPath && window.dataStore) {
                        window.dataStore.set(this.dataPath, numericValue);
                    }
                    
                    // Dispatch custom event
                    this.dispatchEvent(new CustomEvent('value-changed', {
                        detail: { 
                            value: numericValue, 
                            path: this.dataPath,
                            valid: true 
                        }
                    }));
                }
            }
        });
        
        // Handle blur for final validation
        this.input.addEventListener('blur', () => {
            this.validate();
        });
        
        // Handle focus for better UX
        this.input.addEventListener('focus', () => {
            this.input.classList.remove('is-invalid');
            this.feedback.textContent = '';
        });
    }
    
    bindToDataStore() {
        if (!window.dataStore || !this.dataPath) return;
        
        // Bind component to DataStore changes
        window.dataStore.bindComponent(this, this.dataPath, (newValue, oldValue) => {
            if (!this.isUpdating && newValue !== undefined) {
                this.isUpdating = true;
                this.setValue(newValue);
                this.isUpdating = false;
                
                //console.log(`NumberField: Updated from DataStore ${this.dataPath} = ${newValue}`);
            }
        });
        
        // Initialize with current DataStore value
        const currentValue = window.dataStore.get(this.dataPath);
        if (currentValue !== undefined) {
            this.setValue(currentValue);
        } else if (this.initialValue !== null) {
            // Set initial value in DataStore
            window.dataStore.set(this.dataPath, this.initialValue);
        }
    }
    
    // Public methods
    
    getValue() {
        const value = parseFloat(this.input.value);
        return isNaN(value) ? null : value;
    }
    
    setValue(value) {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
            this.input.value = numericValue;
            this.validate();
        }
    }
    
    validate() {
        const value = this.getValue();
        const min = parseFloat(this.input.min);
        const max = parseFloat(this.input.max);
        const required = this.input.hasAttribute('required');
        
        let isValid = true;
        let errorMessage = '';
        
        // Check if required
        if (required && (value === null || value === '')) {
            isValid = false;
            errorMessage = 'This field is required';
        }
        
        // Check min value
        if (isValid && !isNaN(min) && value < min) {
            isValid = false;
            errorMessage = `Value must be at least ${min}`;
        }
        
        // Check max value
        if (isValid && !isNaN(max) && value > max) {
            isValid = false;
            errorMessage = `Value must be no more than ${max}`;
        }
        
        // Update UI based on validation
        if (isValid) {
            this.input.classList.remove('is-invalid');
            this.input.classList.add('is-valid');
            this.feedback.textContent = '';
        } else {
            this.input.classList.remove('is-valid');
            this.input.classList.add('is-invalid');
            this.feedback.textContent = errorMessage;
            
            // Dispatch validation error event
            this.dispatchEvent(new CustomEvent('validation-error', {
                detail: { 
                    value, 
                    error: errorMessage,
                    path: this.dataPath 
                }
            }));
        }
        
        return isValid;
    }
    
    reset() {
        this.setValue(this.initialValue);
        this.input.classList.remove('is-valid', 'is-invalid');
        this.feedback.textContent = '';
    }
    
    // Utility methods
    
    focus() {
        this.input.focus();
    }
    
    blur() {
        this.input.blur();
    }
    
    disable() {
        this.input.disabled = true;
    }
    
    enable() {
        this.input.disabled = false;
    }
}

export { input_number_field };
